import { useCallback, useMemo, useState, type JSX } from "react";
import { SearchRegular, ArrowSyncRegular, AddRegular } from "@fluentui/react-icons";
import { FluentSpinner } from "@/components/fluent-ui";
import { useApi } from "@/hooks/useApi";
import { ProjectsApi } from "@/services/api/projects-api";
import { getCreateDealDisplay, openCreateDealDialog } from "@/services/outlook/create-deal-dialog";
import type { TriyoProject } from "@/types/models";
import { formatLongCalendarDate } from "@/utils/calendar-date";
import { TabSectionHeader } from "@/components/Workflow/TabSectionHeader";
import { CreateDeal } from "@/components/Workflow/QuickActions/CreateDeal";
import "../shared.scss";
import "./Deals.scss";

function projectName(project: TriyoProject): string {
  return project.Name || project.ProjectName || project.Title || "Untitled deal";
}

function projectEndDate(project: TriyoProject): string {
  const raw = project.EndDate || project.DueDate || project.End;
  if (!raw) return "—";
  return formatLongCalendarDate(raw);
}

const INACTIVE_STATUSES = new Set([
  "archived",
  "completed",
  "complete",
  "inactive",
  "in active",
  "closed",
  "cancelled",
  "canceled",
]);

function isActiveProject(project: TriyoProject): boolean {
  if (project.Deleted === true || project.StatusId === 0 || project.StatusId === 2) return false;
  const status = (project.StrStatus || project.StatusName || project.Status || "").trim().toLowerCase();
  return !status || !INACTIVE_STATUSES.has(status);
}

function ownerInitials(project: TriyoProject): string {
  if (project.OwnerInitials) return project.OwnerInitials.slice(0, 2).toUpperCase();
  const name = project.OwnerName || "";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function Deals(): JSX.Element {
  const fetcher = useCallback(() => ProjectsApi.getUserProjects(), []);
  const { data, loading, error, refetch } = useApi(fetcher);
  const [query, setQuery] = useState("");
  const [spinning, setSpinning] = useState(false);
  const [creating, setCreating] = useState(false);

  const startCreating = () => {
    if (getCreateDealDisplay() === "dialog") {
      void openCreateDealDialog().then((result) => {
        if (result === "unavailable") setCreating(true);
      });
      return;
    }
    setCreating(true);
  };

  const deals = useMemo(() => {
    const list = (data ?? []).filter(isActiveProject);
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => {
      const name = projectName(p).toLowerCase();
      const company = (p.CompanyName ?? "").toLowerCase();
      return name.includes(q) || company.includes(q);
    });
  }, [data, query]);

  const refresh = async () => {
    setSpinning(true);
    try {
      await refetch();
    } finally {
      setSpinning(false);
    }
  };

  if (creating) {
    return (
      <div className="tab-panel">
        <TabSectionHeader
          title="Create Deal"
          action={
            <button type="button" className="deals__back" onClick={() => setCreating(false)}>
              Back
            </button>
          }
        />
        <CreateDeal onClose={() => setCreating(false)} onCreated={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="tab-panel">
      <TabSectionHeader
        title="Deals"
        action={
          <button
            type="button"
            aria-label="Refresh"
            title="Refresh"
            className={`icon-button${spinning ? " icon-button--spin" : ""}`}
            disabled={spinning}
            aria-busy={spinning}
            onClick={() => void refresh()}
          >
            <ArrowSyncRegular />
          </button>
        }
      />

      <div className="search-row">
        <div className="search-field">
          <SearchRegular />
          <input
            className="search-field__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for Deals"
            aria-label="Search for Deals"
          />
        </div>
        <button
          type="button"
          className="add-button"
          aria-label="New Deal"
          title="New Deal"
          onClick={startCreating}
        >
          <AddRegular />
        </button>
      </div>

      <div className="list-scroll">
        {loading ? (
          <div className="empty-state" role="status" aria-live="polite">
            <FluentSpinner label="Loading deals…" />
          </div>
        ) : null}

        {!loading && error ? (
          <div className="empty-state" role="alert">
            {error}
          </div>
        ) : null}

        {!loading && !error && deals.length === 0 ? (
          <div className="empty-state" role="status">
            No Deals{query ? " match your search" : ""}
          </div>
        ) : null}

        {!loading &&
          !error &&
          deals.map((deal) => {
            const initials = ownerInitials(deal);
            return (
              <article key={deal.Id ?? deal.ProjectId ?? projectName(deal)} className="deal-card">
                <div className="deal-card__name">{projectName(deal)}</div>
                <div className="deal-card__meta">
                  <span className="deal-card__date">End Date: {projectEndDate(deal)}</span>
                  {initials ? <span className="deal-card__avatar">{initials}</span> : null}
                </div>
              </article>
            );
          })}
      </div>
    </div>
  );
}
